import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";

export interface EditableRowsTableColumn {
  label?: string;
  size: number;
  /** Required marker (asterisk only — the actual validation lives in the form's schema). */
  required?: boolean;
}

export interface EditableRow {
  key: string;
  cells: ReactNode;
}

interface Props {
  columns: EditableRowsTableColumn[];
  rows: EditableRow[];
  addRowLabel: string;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
}

const headerCaptionSx = {
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "text.secondary",
  fontWeight: 600,
} as const;

export function EditableRowsTable({ columns, rows, addRowLabel, onAddRow, onRemoveRow }: Props) {
  const { t } = useTranslation();

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Grid
        container
        columnSpacing={1}
        sx={{ px: 1.5, py: 0.75, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}
      >
        {columns.map((column, index) => (
          <Grid key={index} size={column.size}>
            {column.label && (
              <Typography variant="caption" sx={headerCaptionSx}>
                {column.required ? `${column.label} *` : column.label}
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>

      {rows.length > 0 && (
        <Box sx={{ px: 1, pt: 1 }}>
          {rows.map((row, index) => (
            <Box key={row.key} sx={{ mb: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
              {row.cells}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton size="medium" onClick={() => onRemoveRow(index)} aria-label={t("common.delete")}>
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ m: 1 }}>
        <Button variant="outlined" size="medium" onClick={onAddRow} fullWidth sx={{ borderRadius: 0, py: 0.75 }}>
          {addRowLabel}
        </Button>
      </Box>
    </Box>
  );
}
